<template>
  <div class="technology-page">
    <!-- Hero Banner Section -->
    <HeroCarouselComp 
      :slides="technologySlides"
      background-image="/banner/banner-three.jpg"
      pattern-image="/shapes/shape-06.png"
      :enable-particles="true"
      :mission-vision="technologyMissionVision"
    />

    <!-- Services Section -->
    <TechItsOurOfferingComp :content="offeringsContent" />

    <!-- Our Stack Section -->
    <TechItsOurStackComp :content="stackContent" />

    <!-- Clients & Case Studies Section -->
    <TechItsClientsCaseStudiesComp :content="clientsCaseStudiesContent" />

    <!-- Core Values Section -->
    <TechItsCoreValuesComp :content="coreValuesContent" />

    <!-- Blogs & Articles Section -->
    <TechItsBlogsArticlesComp :content="blogsContent" />

    <!-- Resource Center Section -->
    <TechItsResourceCenterComp :content="resourceCenterContent" />

    <!-- Trusted Companies Section -->
    <TechItsTrustedCompaniesComp :content="trustedCompaniesContent" />
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import WOW from "wowjs";
import HeroCarouselComp from "@/components/HeroCarouselComp.vue";
import TechItsOurOfferingComp from "@/components/TechItsOurOfferingComp.vue";
import TechItsOurStackComp from "@/components/TechItsOurStackComp.vue";
import TechItsClientsCaseStudiesComp from "@/components/TechItsClientsCaseStudiesComp.vue";
import TechItsCoreValuesComp from "@/components/TechItsCoreValuesComp.vue";
import TechItsBlogsArticlesComp from "@/components/TechItsBlogsArticlesComp.vue";
import TechItsResourceCenterComp from "@/components/TechItsResourceCenterComp.vue";
import TechItsTrustedCompaniesComp from "@/components/TechItsTrustedCompaniesComp.vue";

export default {
  name: "TechnologyPage",
  components: {
    HeroCarouselComp,
    TechItsOurOfferingComp,
    TechItsOurStackComp,
    TechItsClientsCaseStudiesComp,
    TechItsCoreValuesComp,
    TechItsBlogsArticlesComp,
    TechItsResourceCenterComp,
    TechItsTrustedCompaniesComp
  },
  setup() {
    const companiesSwiper = ref(null);
    const servicesSwiper = ref(null);
    const testimonialsSwiper = ref(null);
    const isMonthly = ref(true);

    const technologySlides = ref([
      {
        backgroundImage: '/resource/portfolio-04.jpg',
        subtitle: 'Technology that Drives Growth',
        titleLines: [
          { text: 'Technology that ' },
          { text: 'Drives ', highlight: 'Growth' }
        ],
        description: 'Consulting + Engineering that adapts to your business needs.',
        buttonText: 'Discuss',
        buttonLink: '/contact'
      },
      {
        backgroundImage: '/resource/service-details.jpg',
        subtitle: 'Software Architecture',
        titleLines: [
          { text: 'Modern ', highlight: 'Tech Stack' }
        ],
        description: 'Frontend: React, Angular, Vue | Backend: Node.js, Python, Java | Cloud: AWS, Azure, GCP',
        buttonText: 'Learn More',
        buttonLink: '/contact'
      },
      {
        backgroundImage: '/resource/portfolio-01.jpg',
        subtitle: 'Cloud & DevOps',
        titleLines: [
          { text: 'Scalable ', highlight: 'Infrastructure' }
        ],
        description: 'Cloud Migration, DevOps Integration, and Infrastructure as Code for modern applications.',
        buttonText: 'Get Started',
        buttonLink: '/contact'
      },
      {
        backgroundImage: '/resource/portfolio-02.jpg',
        subtitle: 'AI & Machine Learning',
        titleLines: [
          { text: 'Intelligent ', highlight: 'Solutions' }
        ],
        description: 'AI/ML: OpenAI, TensorFlow, LangChain, PyTorch for intelligent automation and insights.',
        buttonText: 'Explore AI',
        buttonLink: '/contact'
      },
      {
        backgroundImage: '/resource/portfolio-03.jpg',
        subtitle: 'Security & Compliance',
        titleLines: [
          { text: 'Secure ', highlight: 'Development' }
        ],
        description: 'Security Planning, Audits, and Compliance management for enterprise-grade applications.',
        buttonText: 'Secure Now',
        buttonLink: '/contact'
      }
    ]);

    const technologyMissionVision = ref({
      vision: {
        title: 'Our Vision',
        description: 'To be the most trusted software innovation partner, building meaningful technology for real-world challenges.'
      },
      mission: {
        title: 'Our Mission',
        description: 'To build meaningful technology for real-world challenges through cutting-edge research, innovative development, and breakthrough discoveries that transform industries.'
      }
    });

    // Offerings content object
    const offeringsContent = ref({
      header: {
        title: "Our Offerings",
        description: "Comprehensive technology consulting and engineering services that adapt to your business needs."
      },
      services: [
        {
          title: "Software Architecture Planning",
          slug: "software-architecture-planning",
          description: "📐 Comprehensive software architecture design and planning services for scalable, maintainable systems.",
          image: "assets/images/service/service_thumb01.jpg",
          icon: "icon-04"
        },
        {
          title: "API Integration & Automation",
          slug: "api-integration-automation",
          description: "🔄 Seamless API integration and automation solutions to streamline your business processes.",
          image: "assets/images/service/service_thumb02.jpg",
          icon: "icon-08"
        },
        {
          title: "Cloud Migration & DevOps",
          slug: "cloud-migration-devops",
          description: "☁️ Complete cloud migration services and DevOps implementation for modern infrastructure.",
          image: "assets/images/service/service_thumb03.jpg",
          icon: "icon-12"
        },
        {
          title: "Product-Market Fit Advisory",
          slug: "product-market-fit-advisory",
          description: "🧩 Strategic advisory services to help you achieve product-market fit and business growth.",
          image: "assets/images/service/service_thumb04.jpg",
          icon: "icon-04"
        },
        {
          title: "Security Planning & Audits",
          slug: "security-planning-audits",
          description: "🔒 Comprehensive security planning, audits, and implementation to protect your digital assets.",
          image: "assets/images/service/service_thumb05.jpg",
          icon: "icon-08"
        }
      ]
    });

    // Stack content object
    const stackContent = ref({
      header: {
        title: "Our Stack",
        description: "Modern technology stack and tools we use to build scalable, efficient, and innovative solutions."
      },
      categories: [
        {
          name: "Frontend",
          technologies: ["React", "Angular", "Vue"]
        },
        {
          name: "Backend",
          technologies: ["Node.js", "Python", "Java"]
        },
        {
          name: "Cloud",
          technologies: ["AWS", "Azure", "GCP"]
        },
        {
          name: "Databases",
          technologies: ["PostgreSQL", "MongoDB", "Redis"]
        },
        {
          name: "AI/ML",
          technologies: ["OpenAI", "TensorFlow", "LangChain", "PyTorch"]
        }
      ]
    });

    // Clients & Case Studies content object
    const clientsCaseStudiesContent = ref({
      header: {
        title: "Clients & Case Studies",
        description: "Real success stories and testimonials from our clients across various industries"
      },
      caseStudies: [
        {
          title: "How Sogdo's Cloud Plan Saved $100K/Year for Client",
          category: "Cloud Migration",
          duration: "3 months",
          icon: "fas fa-cloud",
          description: "Our cloud migration strategy reduced infrastructure costs by 40% while improving performance and scalability for a leading e-commerce platform.",
          stats: [
            {
              value: "$100K",
              label: "Annual Savings"
            },
            {
              value: "40%",
              label: "Cost Reduction"
            },
            {
              value: "99.9%",
              label: "Uptime"
            }
          ],
          technologies: ["AWS", "Docker", "Kubernetes", "Terraform"]
        },
        {
          title: "Migrated legacy fintech systems in 60 days",
          category: "Fintech Modernization",
          duration: "2 months",
          icon: "fas fa-university",
          description: "Successfully modernized legacy banking systems with zero downtime and enhanced security features for a regional financial institution.",
          stats: [
            {
              value: "60",
              label: "Days"
            },
            {
              value: "0%",
              label: "Downtime"
            },
            {
              value: "300%",
              label: "Performance"
            }
          ],
          technologies: ["Java", "Spring Boot", "PostgreSQL", "Redis"]
        }
      ]
    });

    // Core Values content object
    const coreValuesContent = ref({
      header: {
        title: "Our Core Values",
        description: "Driving innovation through technology excellence"
      },
      values: [
        {
          title: "Innovation",
          icon: "fas fa-lightbulb",
          description: "We constantly push boundaries with cutting-edge AI and machine learning solutions that transform businesses."
        },
        {
          title: "Collaboration",
          icon: "fas fa-users",
          description: "We work closely with clients to understand their unique challenges and deliver tailored technology solutions."
        },
        {
          title: "Agility",
          icon: "fas fa-rocket",
          description: "Our rapid development approach ensures quick deployment of scalable solutions that adapt to changing needs."
        },
        {
          title: "Excellence",
          icon: "fas fa-handshake",
          description: "We maintain the highest standards in code quality, security, and performance for every project we deliver."
        }
      ]
    });

    // Blogs content object
    const blogsContent = ref({
      header: {
        title: "Latest Technology Insights",
        description: "Stay updated with the latest trends, insights, and best practices in technology"
      },
      articles: [
        {
          title: "The Future of Artificial Intelligence in Business",
          category: "AI & ML",
          author: "Tech Team",
          date: {
            day: "15",
            month: "Dec"
          },
          image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop",
          excerpt: "Discover how AI is transforming modern businesses and what you need to know to stay competitive in the digital age."
        },
        {
          title: "Cloud Computing Best Practices for Startups",
          category: "Cloud",
          author: "Cloud Team",
          date: {
            day: "10",
            month: "Dec"
          },
          image: require("@/assets/images/news/news-1.jpg"),
          excerpt: "Learn essential cloud strategies that can help your startup scale efficiently while maintaining security and performance."
        },
        {
          title: "Cybersecurity Trends for 2025",
          category: "Security",
          author: "Security Team",
          date: {
            day: "5",
            month: "Dec"
          },
          image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=250&fit=crop",
          excerpt: "Stay ahead of emerging threats with our comprehensive guide to the latest cybersecurity trends and protection strategies."
        }
      ]
    });

    // Resource Center content object
    const resourceCenterContent = ref({
      header: {
        title: "Resource Center",
        description: "Access our comprehensive collection of guides, tools, and resources to accelerate your technology journey"
      },
      resources: [
        {
          title: "Technical Documentation",
          icon: "fas fa-book",
          description: "Comprehensive guides and API documentation for all our services and solutions.",
          linkText: "View Docs"
        },
        {
          title: "Code Samples",
          icon: "fas fa-code",
          description: "Ready-to-use code examples and templates to jumpstart your development projects.",
          linkText: "Browse Samples"
        },
        {
          title: "Best Practices",
          icon: "fas fa-star",
          description: "Industry best practices and guidelines for building scalable, secure applications.",
          linkText: "Learn More"
        },
        {
          title: "Video Tutorials",
          icon: "fas fa-play-circle",
          description: "Step-by-step video tutorials covering various technologies and implementation strategies.",
          linkText: "Watch Now"
        },
        {
          title: "Community Forum",
          icon: "fas fa-users",
          description: "Connect with developers, ask questions, and share knowledge in our community forum.",
          linkText: "Join Community"
        },
        {
          title: "Support Center",
          icon: "fas fa-life-ring",
          description: "Get help with technical issues, troubleshooting, and implementation support.",
          linkText: "Get Support"
        }
      ]
    });

    // Trusted Companies content object
    const trustedCompaniesContent = ref({
      header: {
        title: "Trusted by the world's largest businesses",
        description: "Leading companies across industries trust us with their technology needs"
      },
      companies: [
        {
          name: "Microsoft",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/96/Microsoft_logo_%282012%29.svg/2048px-Microsoft_logo_%282012%29.svg.png"
        },
        {
          name: "Google",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Google_2015_logo.svg/2560px-Google_2015_logo.svg.png"
        },
        {
          name: "Amazon",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Amazon_logo.svg/2560px-Amazon_logo.svg.png"
        },
        {
          name: "Apple",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Apple_logo_black.svg/814px-Apple_logo_black.svg.png"
        },
        {
          name: "Meta",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/Facebook_Logo_%282019%29.png/1200px-Facebook_Logo_%282019%29.png"
        },
        {
          name: "Netflix",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/77/Netflix_logo.svg/2560px-Netflix_logo.svg.png"
        },
        {
          name: "Spotify",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/19/Spotify_logo_without_text.svg/168px-Spotify_logo_without_text.svg.png"
        },
        {
          name: "Tesla",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bb/Tesla_T_symbol.svg/2048px-Tesla_T_symbol.svg.png"
        }
      ]
    });

    onMounted(async () => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false,
      }).init();

      // Initialize companies swiper
      initCompaniesSwiper();
    });


    const initCompaniesSwiper = () => {
      // Wait for Swiper to be available globally
      const checkSwiper = () => {
        if (typeof window !== "undefined" && window.Swiper) {
          new window.Swiper(".companies-swiper", {
            slidesPerView: 1,
            spaceBetween: 30,
            loop: true,
            autoplay: {
              delay: 3000,
              disableOnInteraction: false,
            },
            breakpoints: {
              640: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 3,
                spaceBetween: 30,
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 40,
              },
            },
          });
        } else {
          setTimeout(checkSwiper, 100);
        }
      };
      checkSwiper();
    };

    return {
      companiesSwiper,
      servicesSwiper,
      testimonialsSwiper,
      isMonthly,
      technologySlides,
      technologyMissionVision,
      offeringsContent,
      stackContent,
      clientsCaseStudiesContent,
      coreValuesContent,
      blogsContent,
      resourceCenterContent,
      trustedCompaniesContent
    };
  },
  data() {
    return {
      swiper: null,
      activeTab: 0,
      resourceTabs: [
        { id: 1, name: "Insights" },
        { id: 2, name: "News" },
        { id: 3, name: "Analyst Notes" },
        { id: 4, name: "White Papers" },
        { id: 5, name: "Podcast" },
      ],
      resourceData: {
        insights: [
          {
            id: 1,
            title:
              "Cloud-Native Architecture: Building Scalable Applications for the Future",
            description:
              "Discover how cloud-native architecture is revolutionizing application development with microservices, containers, and serverless computing...",
            image: require("@/assets/images/resource/portfolio-01.jpg"),
            category: "Tech Insights",
          },
          {
            id: 2,
            title:
              "Machine Learning in Production: Best Practices for Enterprise Deployment",
            description:
              "Learn essential strategies for deploying machine learning models in production environments with focus on scalability, monitoring, and reliability...",
            image: require("@/assets/images/resource/portfolio-02.jpg"),
            category: "AI Research",
          },
          {
            id: 3,
            title:
              "Zero Trust Security Model: Implementing Comprehensive Network Protection",
            description:
              "Explore the zero trust security framework and how it's transforming enterprise cybersecurity with never trust, always verify principles...",
            image: require("@/assets/images/resource/portfolio-03.jpg"),
            category: "Security",
          },
        ],
        news: [
          {
            id: 4,
            title: "Latest AI Breakthroughs: GPT-4 and Beyond",
            description:
              "Stay updated with the latest developments in artificial intelligence, including new language models and their business applications...",
            image: require("@/assets/images/resource/portfolio-04.jpg"),
            category: "AI News",
          },
          {
            id: 5,
            title: "Quantum Computing Advances: What It Means for Business",
            description:
              "Explore how quantum computing is evolving and the potential impact on cybersecurity, optimization, and data processing...",
            image: require("@/assets/images/resource/gallery-01.png"),
            category: "Quantum Tech",
          },
          {
            id: 6,
            title: "5G Network Expansion: Global Infrastructure Updates",
            description:
              "Learn about the latest 5G network deployments worldwide and how they're enabling new IoT and edge computing applications...",
            image: require("@/assets/images/resource/gallery-02.png"),
            category: "Network Tech",
          },
        ],
        analyst: [
          {
            id: 7,
            title: "Market Analysis: Cloud Computing Trends 2024",
            description:
              "Comprehensive analysis of cloud computing market trends, including adoption rates, cost optimization strategies, and future projections...",
            image: require("@/assets/images/resource/gallery-03.png"),
            category: "Market Analysis",
          },
          {
            id: 8,
            title: "Cybersecurity Investment: ROI and Best Practices",
            description:
              "Detailed analysis of cybersecurity investments, including cost-benefit analysis, risk assessment, and implementation strategies...",
            image: require("@/assets/images/resource/gallery-04.png"),
            category: "Security Analysis",
          },
          {
            id: 9,
            title: "Digital Transformation: Success Metrics and KPIs",
            description:
              "Learn how to measure digital transformation success with key performance indicators and success metrics for technology initiatives...",
            image: require("@/assets/images/resource/gallery-05.png"),
            category: "Transformation",
          },
        ],
        papers: [
          {
            id: 10,
            title: "White Paper: Enterprise AI Implementation Guide",
            description:
              "Comprehensive guide to implementing artificial intelligence in enterprise environments, including strategy, planning, and execution...",
            image: require("@/assets/images/resource/choose-01.png"),
            category: "AI Implementation",
          },
          {
            id: 11,
            title: "White Paper: Cloud Security Best Practices",
            description:
              "Detailed security guidelines for cloud environments, covering data protection, access control, and compliance requirements...",
            image: require("@/assets/images/resource/choose-02.svg.svg"),
            category: "Cloud Security",
          },
          {
            id: 12,
            title: "White Paper: DevOps Culture and Implementation",
            description:
              "Learn how to build and implement a successful DevOps culture in your organization, including tools, processes, and team structures...",
            image: require("@/assets/images/resource/choose-03.png"),
            category: "DevOps",
          },
        ],
        podcast: [
          {
            id: 13,
            title: "Tech Talk: Future of Web Development",
            description:
              "Join our experts as they discuss the latest trends in web development, including new frameworks, tools, and best practices...",
            image: require("@/assets/images/resource/choose-04.png"),
            category: "Web Development",
          },
          {
            id: 14,
            title: "Tech Talk: Blockchain and Cryptocurrency Trends",
            description:
              "Explore the latest developments in blockchain technology and cryptocurrency markets with industry experts and thought leaders...",
            image: require("@/assets/images/resource/base.png"),
            category: "Blockchain",
          },
          {
            id: 15,
            title: "Tech Talk: Data Science and Analytics",
            description:
              "Discover how data science is transforming business decision-making with advanced analytics, machine learning, and data visualization...",
            image: require("@/assets/images/resource/about-us.png"),
            category: "Data Science",
          },
        ],
      },
    };
  },
  computed: {
    currentTabResources() {
      const tabNames = ["insights", "news", "analyst", "papers", "podcast"];
      const currentTabName = tabNames[this.activeTab];
      return this.resourceData[currentTabName] || [];
    },
  },
};
</script>

<style scoped>
/* Import Google Fonts */
@import url("https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap");

/* Swiper CSS will be loaded via CDN */

/* Home Three Banner Styles */
.technology-page .home__three__banner {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
}


.technology-page .home__three__banner .image-layer {
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
}

.technology-page .home__three__banner .banner__data {
  padding: 0;
  position: relative;
  z-index: 3;
}

.technology-page .home__three__banner h1 {
  color: #fff;
  font-family: "Archivo", sans-serif;
  font-size: 80px;
  font-weight: 700;
  line-height: 90px;
  margin-bottom: 30px;
}

.technology-page .home__three__banner h1 span {
  font-weight: 400;
}

.technology-page .home__three__banner h1 span.color {
  color: #f6ec4f;
}

.technology-page .home__three__banner .sub__title h4 {
  color: #f6ec4f;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-bottom: 20px;
}

.technology-page .home__three__banner .text p {
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
  line-height: 28px;
  margin-bottom: 40px;
  font-family: "Open Sans", sans-serif;
}


/* Pattern Layer */
.technology-page .pattern-3 {
  width: 299px;
  height: 411px;
  left: 233px;
  bottom: 240px;
  background-repeat: no-repeat;
  background-size: contain;
}





/* Core Values Section */
.core-values-section {
  padding: 100px 0;
  background: linear-gradient(145deg, #0a0f1c, #1a2332);
  color: #fff;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 15px;
  position: relative;
}

.section-header h2::after {
  content: "";
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(135deg, #f6ec4f, #ffd700);
  border-radius: 2px;
}

.section-header p {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 20px;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.value-card {
  background: rgba(255, 255, 255, 0.05);
  padding: 40px 30px;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(246, 236, 79, 0.1);
}

.value-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
  border-color: rgba(246, 236, 79, 0.3);
  background: rgba(255, 255, 255, 0.08);
}

.value-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 25px;
  background: linear-gradient(135deg, #f6ec4f, #ffd700);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.value-card:hover .value-icon {
  transform: scale(1.1);
  box-shadow: 0 10px 25px rgba(246, 236, 79, 0.4);
}

.value-icon i {
  font-size: 32px;
  color: #000;
}

.value-card h3 {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 15px;
}

.value-card p {
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

/* Responsive Design */
@media only screen and (max-width: 1200px) {
  .values-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  .blogs-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 25px;
  }

  .resource-grid-inner {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }
}

@media only screen and (max-width: 767px) {
  .core-values-section {
    padding: 60px 0;
  }

  .section-header h2 {
    font-size: 36px;
  }

  .values-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .value-card {
    padding: 30px 20px;
  }

  .value-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 20px;
  }

  .value-icon i {
    font-size: 24px;
  }

  .value-card h3 {
    font-size: 20px;
  }

  .value-card p {
    font-size: 14px;
  }

  .blogs-section {
    padding: 60px 0;
  }

  .blogs-section .section-header h2 {
    font-size: 36px;
  }

  .blogs-grid {
    grid-template-columns: 1fr !important;
    gap: 20px;
  }

  .blog-content {
    padding: 20px;
  }

  .blog-content h4 {
    font-size: 18px;
  }

  .blog-content p {
    font-size: 14px;
  }

  .trusted-companies-section {
    padding: 60px 0;
  }

  .trusted-companies-section .section-header h2 {
    font-size: 36px;
  }

  .company-logo {
    height: 100px;
    padding: 15px;
  }

  .company-logo img {
    max-height: 60px;
  }

  .resource-grid-inner {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .companies-slider {
    padding: 0 20px;
  }

  .swiper-button-prev {
    left: 5px;
  }

  .swiper-button-next {
    right: 5px;
  }
}

/* Blogs & Articles Section */
.blogs-section {
  padding: 100px 0;
  background: linear-gradient(145deg, #0a0f1c, #1a2332);
  color: #fff;
}

.blogs-section .section-header {
  text-align: center;
  margin-bottom: 60px;
}

.blogs-section .section-header h3 {
  font-size: 18px;
  color: #f6ec4f;
  font-weight: 600;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.blogs-section .section-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.blogs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.blog-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(246, 236, 79, 0.1);
}

.blog-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
  border-color: rgba(246, 236, 79, 0.3);
  background: rgba(255, 255, 255, 0.08);
}

.blog-image {
  width: 100%;
  height: 250px;
  overflow: hidden;
}

.blog-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.blog-card:hover .blog-image img {
  transform: scale(1.05);
}

.blog-content {
  padding: 25px;
}

.blog-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.blog-meta i {
  color: #f6ec4f;
}

.separator {
  color: rgba(255, 255, 255, 0.3);
}

.blog-content h4 {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 15px;
  line-height: 1.4;
}

.blog-content p {
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 20px;
}

.read-more {
  color: #f6ec4f;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: color 0.3s ease;
}

.read-more:hover {
  color: #ffd700;
}

.view-all-btn {
  text-align: center;
  margin-top: 50px;
}

.btn-view-all {
  display: inline-block;
  background: #f6ec4f;
  color: #000;
  padding: 15px 30px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-view-all:hover {
  background: #ffd700;
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(246, 236, 79, 0.4);
}

/* Resource Center Section */
.resource-center-section {
  padding: 100px 0;
  background: linear-gradient(145deg, #0a0f1c, #1a2332);
  color: #fff;
}

.resource-center-section .section-header {
  text-align: center;
  margin-bottom: 50px;
}

.resource-center-section .section-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.resource-tabs {
  display: flex;
  justify-content: center;
  gap: 0;
  margin-bottom: 60px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.tab-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  padding: 15px 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  border-bottom: 3px solid transparent;
  transform: translateY(0);
}

.tab-btn:hover {
  color: #f6ec4f;
  transform: translateY(-2px);
}

.tab-btn.active {
  color: #f6ec4f;
  border-bottom-color: #f6ec4f;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(246, 236, 79, 0.2);
}

.resource-grid {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  min-height: 400px;
}

.resource-grid-inner {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

/* Smooth Tab Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.95);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.resource-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(246, 236, 79, 0.1);
}

.resource-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
  border-color: rgba(246, 236, 79, 0.3);
}

.resource-image {
  position: relative;
  width: 100%;
  height: 250px;
  overflow: hidden;
}

.resource-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.resource-card:hover .resource-image img {
  transform: scale(1.05);
}

.resource-overlay {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(246, 236, 79, 0.9);
  color: #000;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.resource-content {
  padding: 25px;
}

.resource-content h4 {
  font-size: 20px;
  font-weight: 700;
  color: #f6ec4f;
  margin-bottom: 15px;
  line-height: 1.4;
}

.resource-content p {
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 20px;
}

.resource-link {
  color: #f6ec4f;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: color 0.3s ease;
}

.resource-link:hover {
  color: #ffd700;
}

.resource-center-section .view-all-btn {
  text-align: center;
  margin-top: 50px;
}

.resource-center-section .btn-view-all {
  display: inline-block;
  background: #f6ec4f;
  color: #000;
  padding: 15px 30px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.resource-center-section .btn-view-all:hover {
  background: #ffd700;
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(246, 236, 79, 0.4);
}

/* Trusted Companies Section */
.trusted-companies-section {
  padding: 100px 0;
  background: linear-gradient(145deg, #0a0f1c, #1a2332);
  color: #fff;
}

.trusted-companies-section .section-header {
  text-align: center;
  margin-bottom: 60px;
}

.trusted-companies-section .section-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.companies-slider {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 60px;
  position: relative;
}

.company-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(246, 236, 79, 0.1);
}

.company-logo:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.company-logo img {
  max-width: 100%;
  max-height: 80px;
  object-fit: contain;
  filter: grayscale(100%);
  transition: all 0.3s ease;
}

.company-logo:hover img {
  filter: grayscale(0%);
}

/* Owl Carousel Navigation for Companies */
/* Swiper Navigation Buttons */
.swiper-button-next,
.swiper-button-prev {
  background: #f6ec4f;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  color: #000;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(246, 236, 79, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 64%;
  transform: translateY(-50%);
  z-index: 10;
}

.swiper-button-next:hover,
.swiper-button-prev:hover {
  background: #ffd700;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 8px 25px rgba(246, 236, 79, 0.4);
}

.swiper-button-prev {
  left: 10px;
}

.swiper-button-next {
  right: 10px;
}

.swiper-button-next:after,
.swiper-button-prev:after {
  font-family: "Font Awesome 6 Free";
  font-weight: 900;
  font-size: 16px;
}

.swiper-button-prev:after {
  content: "\f053";
}

.swiper-button-next:after {
  content: "\f054";
}

@media only screen and (max-width: 991px) {
  .technology-page .home__three__banner {
    padding-top: 100px;
  }

  .technology-page .home__three__banner h1 {
    font-size: 60px;
    line-height: 70px;
  }
}

@media only screen and (max-width: 767px) {
  .technology-page .home__three__banner h1 {
    font-size: 50px;
    line-height: 60px;
  }

  .technology-page .home__three__banner .sub__title h4 {
    font-size: 16px;
    letter-spacing: 5.2px;
  }


}

</style>
